"use client"

import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { Spinner, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@t-one-internal/design-system';
import { ResetPasswordButton } from '../../../components/profile/ResetPasswordButton';
import TwoFactorSetupModal from '../../../components/TwoFactorSetupModal';
import { getUserTwoFactorStatus, disableTwoFactorForUser, adminUpdateUserOtpPreference } from '../../../services/twoFactorApi';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';

interface TwoFactorStatus {
  is_2fa_enabled: boolean;
  is_setup_complete: boolean;
  is_mfa_mandatory: boolean;
  is_admin_enabled: boolean;
  otp_preference: string;
}

interface AdminSectionProps {
  employee: EmployeeDetails;
}

/**
 * AdminSection for self-mode My Profile.
 * Stripped down to only show:
 * - Two-Factor Authentication (2FA) toggle + setup
 * - Password Reset
 */
export const AdminSection: React.FC<AdminSectionProps> = ({ employee }) => {
  // 2FA State
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null);
  const [isLoadingTwoFactor, setIsLoadingTwoFactor] = useState(false);
  const [isUpdatingTwoFactor, setIsUpdatingTwoFactor] = useState(false);
  const [isUpdatingOtpPreference, setIsUpdatingOtpPreference] = useState(false);
  const [isTwoFactorSetupModalOpen, setIsTwoFactorSetupModalOpen] = useState(false);

  // Fetch 2FA status
  useEffect(() => {
    const fetchTwoFactorStatus = async () => {
      if (!employee.user_uuid) return;
      setIsLoadingTwoFactor(true);
      try {
        const response = await getUserTwoFactorStatus(employee.user_uuid);
        if (response?.data) {
          setTwoFactorStatus({
            is_2fa_enabled: response.data.is_2fa_enabled,
            is_setup_complete: response.data.is_setup_complete,
            is_mfa_mandatory: response.data.is_mfa_mandatory ?? false,
            is_admin_enabled: !!response.data.enabled_by,
            otp_preference: response.data.otp_preference ?? 'totp',
          });
        }
      } catch (error) {
        console.error('Failed to fetch 2FA status:', error);
      } finally {
        setIsLoadingTwoFactor(false);
      }
    };

    fetchTwoFactorStatus();
  }, [employee.user_uuid]);

  // Toggle 2FA
  const handleToggleTwoFactor = async () => {
    if (!employee.user_uuid || isUpdatingTwoFactor || isLoadingTwoFactor) return;

    // If 2FA is mandatory or admin-enabled, don't allow self-disable
    if (twoFactorStatus?.is_mfa_mandatory || twoFactorStatus?.is_admin_enabled) return;

    const currentlyEnabled = twoFactorStatus?.is_2fa_enabled || twoFactorStatus?.is_mfa_mandatory;

    if (!currentlyEnabled) {
      // Enabling 2FA - show setup modal
      setIsTwoFactorSetupModalOpen(true);
      return;
    }

    // Disabling 2FA
    setIsUpdatingTwoFactor(true);
    try {
      await disableTwoFactorForUser(employee.user_uuid, employee.user_uuid);
      setTwoFactorStatus(prev => prev ? { ...prev, is_2fa_enabled: false, is_setup_complete: false } : null);
    } catch (error) {
      console.error('Failed to toggle 2FA:', error);
    } finally {
      setIsUpdatingTwoFactor(false);
    }
  };

  // Handle self-setup completion
  const handleSelfSetupComplete = async () => {
    setIsTwoFactorSetupModalOpen(false);
    // Refresh 2FA status
    if (employee.user_uuid) {
      try {
        const response = await getUserTwoFactorStatus(employee.user_uuid);
        if (response?.data) {
          setTwoFactorStatus({
            is_2fa_enabled: response.data.is_2fa_enabled,
            is_setup_complete: response.data.is_setup_complete,
            is_mfa_mandatory: response.data.is_mfa_mandatory ?? false,
            is_admin_enabled: !!response.data.enabled_by,
            otp_preference: response.data.otp_preference ?? 'totp',
          });
        }
      } catch (error) {
        console.error('Failed to refresh 2FA status:', error);
      }
    }
  };

  const handleSelfSetupCancel = () => {
    setIsTwoFactorSetupModalOpen(false);
  };

  // Handle OTP preference change
  const handleOtpPreferenceChange = async (preference: 'email' | 'totp') => {
    if (!employee.user_uuid || isUpdatingOtpPreference || isLoadingTwoFactor) return;
    if (twoFactorStatus?.is_mfa_mandatory) return;

    setIsUpdatingOtpPreference(true);
    try {
      await adminUpdateUserOtpPreference(employee.user_uuid, preference);
      setTwoFactorStatus(prev => prev ? { ...prev, otp_preference: preference } : null);
    } catch (error) {
      console.error('Failed to update OTP preference:', error);
    } finally {
      setIsUpdatingOtpPreference(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Password Reset Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900">Password</h3>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Reset Password</span>
            <span className="text-xs text-gray-400">Send a password reset link to your email</span>
          </div>
          {employee.email && <ResetPasswordButton userEmail={employee.email} />}
        </div>
      </div>

      {/* Separator */}
      <div className="border-t border-gray-200" />

      {/* 2FA Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-900">Security</h3>

        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Two-Factor Authentication (2FA)
            </span>
            <span className="text-xs text-gray-400">
              {isLoadingTwoFactor ? 'Loading...' : (
                twoFactorStatus?.is_2fa_enabled || twoFactorStatus?.is_mfa_mandatory
                  ? (twoFactorStatus?.is_setup_complete ? 'Active - Setup complete' : 'Enabled - Setup required')
                  : 'Require TOTP authentication during login'
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(twoFactorStatus?.is_2fa_enabled || twoFactorStatus?.is_mfa_mandatory) && (
              <span className={`text-xs px-2 py-1 rounded-full ${twoFactorStatus?.is_setup_complete
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
                }`}>
                {twoFactorStatus?.is_setup_complete ? 'Active' : 'Setup Required'}
              </span>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleToggleTwoFactor}
                    disabled={isUpdatingTwoFactor || isLoadingTwoFactor || twoFactorStatus?.is_mfa_mandatory || twoFactorStatus?.is_admin_enabled || isTwoFactorSetupModalOpen}
                    aria-pressed={Boolean(twoFactorStatus?.is_2fa_enabled || twoFactorStatus?.is_mfa_mandatory)}
                    aria-label="Toggle two-factor authentication"
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${(twoFactorStatus?.is_2fa_enabled || twoFactorStatus?.is_mfa_mandatory) ? 'bg-blue-600' : 'bg-gray-200'
                      } ${(isUpdatingTwoFactor || isLoadingTwoFactor || twoFactorStatus?.is_mfa_mandatory || twoFactorStatus?.is_admin_enabled) ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${(twoFactorStatus?.is_2fa_enabled || twoFactorStatus?.is_mfa_mandatory) ? 'translate-x-4' : 'translate-x-0.5'
                        }`}
                    />
                  </button>
                </TooltipTrigger>
                {(twoFactorStatus?.is_mfa_mandatory || twoFactorStatus?.is_admin_enabled) && (
                  <TooltipContent>
                    <p>{twoFactorStatus?.is_mfa_mandatory
                      ? 'Your organisation has mandated 2FA for all employees.'
                      : 'MFA was enabled by an administrator. Only an admin can disable it.'
                    }</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* OTP Preference */}
        {(twoFactorStatus?.is_2fa_enabled || twoFactorStatus?.is_mfa_mandatory) && (
          <div className={`space-y-3 px-4 py-3 bg-gray-50 rounded-lg ml-4 ${twoFactorStatus?.is_mfa_mandatory ? 'opacity-80' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-xs font-medium text-gray-700">OTP Preference</span>
                <span className="text-[11px] text-gray-500 mt-0.5">Choose how you receive verification codes</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={`otpPreference-${employee.user_uuid}`}
                  value="email"
                  checked={twoFactorStatus?.otp_preference === 'email'}
                  onChange={() => handleOtpPreferenceChange('email')}
                  disabled={isUpdatingOtpPreference || isLoadingTwoFactor || twoFactorStatus?.is_mfa_mandatory}
                  className="form-radio h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-xs text-gray-700">Email</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={`otpPreference-${employee.user_uuid}`}
                  value="totp"
                  checked={twoFactorStatus?.otp_preference === 'totp' || !twoFactorStatus?.otp_preference}
                  onChange={() => handleOtpPreferenceChange('totp')}
                  disabled={isUpdatingOtpPreference || isLoadingTwoFactor || twoFactorStatus?.is_mfa_mandatory}
                  className="form-radio h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-xs text-gray-700">Authenticator App</span>
              </label>
              {isUpdatingOtpPreference && <Spinner size="xs" className="text-blue-600" />}
            </div>
          </div>
        )}
      </div>

      <TwoFactorSetupModal
        isOpen={isTwoFactorSetupModalOpen}
        onClose={handleSelfSetupCancel}
        onSetupComplete={handleSelfSetupComplete}
        userEmail={employee?.email}
      />
    </div>
  );
};

export default AdminSection;
